/**
 * workbookRoles.ts
 * Classifies sheets in a macro-style workbook the way the VBA macros do:
 *
 *   - printTemplate  – layout sheets that get filled and exported as PDF
 *   - printList      – the short list of codes to print (MÃ CH…)
 *   - data           – the flat table the templates draw from (CONG THUC…)
 *
 * The browser app still binds through DOCX markers, but detecting these roles
 * lets the wizard auto-pick the right data sheet, group column and print queue
 * instead of wrongly treating the first sheet (usually a print form) as data.
 */

import { isRowMetaKey } from "@/types/excel";

export interface SheetLike {
  name: string;
  headers: string[];
  rows: Record<string, unknown>[];
}

export type SheetRole = "data" | "printList" | "printTemplate" | "other";

function deaccent(text: string): string {
  // "đ"/"Đ" are not decomposed by NFD, so replace them explicitly.
  return text
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "");
}

const DATA_NAME_HINTS = ["congthuc", "con gthuc", "data", "dulieu", "dulieugoc", "booking"];

const PRINT_TEMPLATE_NAME_HINTS = [
  "phieugiaohang",
  "bienban",
  "bangke",
  "kiemdich",
  "formvm",
  "giaythongtin",
  "nguongoc",
];

const LIST_NAME_HINTS = ["danhsach", "canin", "print"];

export function classifySheetRole(sheet: SheetLike): SheetRole {
  const name = deaccent(sheet.name);

  if (LIST_NAME_HINTS.some((h) => name.includes(h.replace(/\s+/g, "")))) {
    return "printList";
  }
  if (PRINT_TEMPLATE_NAME_HINTS.some((h) => name.includes(h))) {
    return "printTemplate";
  }
  if (DATA_NAME_HINTS.some((h) => name.includes(h.replace(/\s+/g, "")))) {
    return "data";
  }
  return "other";
}

/** How "data-like" a sheet is: more headers + more rows → higher score. */
function dataShapeScore(sheet: SheetLike): number {
  const cols = sheet.headers.filter((h) => !isRowMetaKey(h)).length;
  return cols * 10 + Math.min(sheet.rows.length, 500);
}

/**
 * Pick the sheet that should feed the bind. Known print templates and print
 * lists are excluded; a sheet named like data wins; otherwise the richest
 * tabular sheet is chosen.
 */
export function suggestDataSheet(sheets: SheetLike[]): string {
  if (sheets.length === 0) return "";

  const eligible = sheets.filter((s) => {
    const role = classifySheetRole(s);
    return role !== "printTemplate" && role !== "printList" && s.rows.length > 0;
  });
  const pool = eligible.length > 0 ? eligible : sheets.filter((s) => s.rows.length > 0);
  if (pool.length === 0) return sheets[0]?.name ?? "";

  const named = pool.filter((s) => classifySheetRole(s) === "data");
  if (named.length > 0) {
    return named.sort((a, b) => dataShapeScore(b) - dataShapeScore(a))[0].name;
  }
  return pool.sort((a, b) => dataShapeScore(b) - dataShapeScore(a))[0].name;
}

const GROUP_COLUMN_HINTS = [
  "madiemgiao",
  "mach",
  "sopo",
  "madonhang",
  "ordercode",
  "ponumber",
];

/**
 * Suggest a grouping column from the data sheet by common name patterns
 * (Mã điểm giao, Số PO…). The user still builds the print list by ticking
 * groups in the UI — nothing is read from a "danh sách cần in" sheet.
 */
export function suggestGroupingColumns(dataSheet: SheetLike | null): string[] {
  if (!dataSheet) return [];
  for (const hint of GROUP_COLUMN_HINTS) {
    const hit = dataSheet.headers.find(
      (h) => !isRowMetaKey(h) && deaccent(h).replace(/\s+/g, "").includes(hint)
    );
    if (hit) return [hit];
  }
  return [];
}

export interface WorkbookClassification {
  dataSheet: string;
  groupingColumns: string[];
  printTemplateSheets: string[];
  /** One-line summary for the upload step. */
  summary: string;
}

/**
 * One-shot classification for a freshly uploaded workbook: which sheet is data,
 * which sheets are Excel print forms to ignore, and a grouping-column hint.
 */
export function classifyWorkbook(sheets: SheetLike[]): WorkbookClassification {
  const dataSheet = suggestDataSheet(sheets);
  const data = sheets.find((s) => s.name === dataSheet) ?? null;
  const groupingColumns = suggestGroupingColumns(data);

  const printTemplateSheets = sheets
    .filter((s) => classifySheetRole(s) === "printTemplate")
    .map((s) => s.name);

  const parts: string[] = [];
  if (dataSheet) parts.push(`Dữ liệu: ${dataSheet}`);
  if (groupingColumns.length > 0) {
    parts.push(`Gom nhóm gợi ý: ${groupingColumns.join(" | ")}`);
  }
  if (printTemplateSheets.length > 0) {
    parts.push(
      `${printTemplateSheets.length} sheet mẫu in (Excel) — bỏ qua khi chọn nguồn dữ liệu; danh sách cần in do bạn chọn ở bước Ánh xạ`
    );
  }

  return {
    dataSheet,
    groupingColumns,
    printTemplateSheets,
    summary: parts.join(" · "),
  };
}
